import type { Dispatch } from 'react';
import { useMemo } from 'react';

import { padColumns } from 'utils/pad-columns';

import { useGetVirtualBankStatements } from 'api';
import type { StatementItem } from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { apiParams } from './apiAdapter';
import { getColumns } from './columns';
import type { Action, LocalState } from './localState';

import { EXPORTED_STATEMENTS_TABLE_TEST_ID } from '../constants';

type Props = {
  localState: LocalState;
  updateCursorAfter: (value: string | null) => void;
  dispatch: Dispatch<Action>;
  downloadReport: (uuid: number) => void;
};

export const ExportedStatementsTable = ({
  localState,
  updateCursorAfter,
  downloadReport
}: Props) => {
  const organisationId = useOrganisationId<number>();

  const { filters: globalFilters } = useGlobalFilters();
  const { findAccountByAccountNumber } = useVirtualAccounts();

  const { data: statements, isFetching } = useGetVirtualBankStatements(
    organisationId,
    apiParams({ ...localState, ...globalFilters }),
    { query: { refetchOnMount: true } }
  );

  const { onScroll, data } = useVirtualList<StatementItem>({
    data: statements?.data,
    uniqueKey: 'id',
    isFetching,
    initialRefValue: String(statements?.meta?.cursor_next),
    updateCursorCallback: updateCursorAfter,
    resetConditions: [globalFilters, localState.filters, updateCursorAfter],
    cursorNext: statements?.meta?.has_next_page
      ? String(statements?.meta?.cursor_next)
      : undefined
  });

  const statementsColumns = useMemo(
    () => padColumns(getColumns(findAccountByAccountNumber, downloadReport)),
    [findAccountByAccountNumber, downloadReport]
  );

  return (
    <DataTable
      total={statements?.meta?.total_count}
      dataSource={data}
      columns={statementsColumns}
      onScroll={onScroll}
      isLoading={isFetching}
      rowKey="id"
      tableDataTestId={EXPORTED_STATEMENTS_TABLE_TEST_ID}
    />
  );
};
